import {
  computed,
  ref,
  type Ref,
} from 'vue'

type LegacyAutocompleteKeyboardEvent = Pick<KeyboardEvent, 'key' | 'preventDefault' | 'stopPropagation'>

type LegacyAutocompleteOptions<T> = {
  selectItem?: (item: T) => void
}

export function nextAutocompleteIndex(currentIndex: number, suggestionCount: number): number {
  if (suggestionCount <= 0) return -1
  return currentIndex >= suggestionCount - 1 ? 0 : currentIndex + 1
}

export function previousAutocompleteIndex(currentIndex: number, suggestionCount: number): number {
  if (suggestionCount <= 0) return -1
  return currentIndex <= 0 ? suggestionCount - 1 : currentIndex - 1
}

export function autocompleteHasSuggestions<T>(suggestions: T[]): boolean {
  return suggestions.length > 0
}

export function resetAutocompleteState<T>(suggestions: Ref<T[]>, activeIndex: Ref<number>): void {
  suggestions.value = []
  activeIndex.value = -1
}

export function handleLegacyAutocompleteKeydown<T>(
  event: LegacyAutocompleteKeyboardEvent,
  suggestions: T[],
  activeIndex: Ref<number>,
  selectItem: (item: T) => void,
): boolean {
  if (event.key === 'Escape' && autocompleteHasSuggestions(suggestions)) {
    event.preventDefault()
    event.stopPropagation()
    suggestions.splice(0)
    activeIndex.value = -1
    return true
  }

  if (!autocompleteHasSuggestions(suggestions)) return false

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    event.stopPropagation()
    activeIndex.value = nextAutocompleteIndex(activeIndex.value, suggestions.length)
    return true
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    event.stopPropagation()
    activeIndex.value = previousAutocompleteIndex(activeIndex.value, suggestions.length)
    return true
  }

  if (event.key === 'Enter' && activeIndex.value >= 0) {
    event.preventDefault()
    event.stopPropagation()
    const selectedSuggestion = suggestions[activeIndex.value]
    if (selectedSuggestion === undefined) return false

    selectItem(selectedSuggestion)
    return true
  }

  return false
}

export function useLegacyAutocomplete<T>(options: LegacyAutocompleteOptions<T> = {}) {
  const suggestions = ref<T[]>([]) as Ref<T[]>
  const activeIndex = ref(-1)
  const hasSuggestions = computed(() => autocompleteHasSuggestions(suggestions.value))

  function setSuggestions(items: T[]): void {
    suggestions.value = items
    activeIndex.value = -1
  }

  function clearSuggestions(): void {
    resetAutocompleteState(suggestions, activeIndex)
  }

  function resetActiveIndex(): void {
    activeIndex.value = -1
  }

  function setActiveIndex(index: number): void {
    activeIndex.value = index
  }

  function handleKeydown(
    event: LegacyAutocompleteKeyboardEvent,
    selectItem = options.selectItem,
  ): boolean {
    if (!selectItem) return false
    return handleLegacyAutocompleteKeydown(event, suggestions.value, activeIndex, selectItem)
  }

  return {
    suggestions,
    activeIndex,
    hasSuggestions,
    setSuggestions,
    clearSuggestions,
    resetActiveIndex,
    setActiveIndex,
    handleKeydown,
  }
}
